import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import ComplexTranslate from 'components/ComplexTranslate';
import { CountDown } from 'components/common/CountDown/CountDown';
import {
  CONFIRMATION_HANDLE_TYPES,
  RESEND_CODE_ERROR_CODE_DISABLE,
  RESEND_ONLY_ERROR_CODE_DISABLE,
  TWO_FACTOR_AUTH_TYPES
} from 'components/common/constants';
import { getTimerDurationSecondsLeft } from 'components/utils';
import { getConfirmationActionType, getTimerDurationExpiresAt } from 'services/authUtils';
import Alert from 'uikit/Alert/Alert';
import { SecurityInput } from 'uikit/SecurityInput/SecurityInput';
import './ConfirmCodeWrapper.scss';

const RESEND_TIMEOUT = 60;

export const ConfirmCodeWrapper = ({
  type = getConfirmationActionType(),
  PhoneComponent,
  isDisabled,
  error,
  onComplete,
  showCountDown,
  handleResendCode,
  onTimerEnd,
  alertState,
  isSwitchToAnotherConfirmationMethod = false,
  isMessenger = false,
  isCardCreationForm = false,
  isGoogle2FASignUpPopUp = false,
  handleSwitchToAnotherMethod,
  confirmationHandleType = CONFIRMATION_HANDLE_TYPES.ON_COMPLETE,
  hideDescription = false
}) => {
  const i18n = useContext(i18nContext);
  const timerDurationExpiresAt = getTimerDurationExpiresAt();
  const [timerDurationSecondsLeft, setTimerDurationSecondsLeft] = useState(
    timerDurationExpiresAt && timerDurationExpiresAt !== 'undefined'
      ? getTimerDurationSecondsLeft(timerDurationExpiresAt)
      : RESEND_TIMEOUT
  );
  const [isDisabledResendCode, setIsDisabledResendCode] = useState(
    RESEND_CODE_ERROR_CODE_DISABLE?.includes(error?.code)
  );

  useEffect(() => {
    if (timerDurationExpiresAt && timerDurationExpiresAt !== 'undefined') {
      setTimerDurationSecondsLeft(getTimerDurationSecondsLeft(timerDurationExpiresAt));
    } else {
      setTimerDurationSecondsLeft(RESEND_TIMEOUT);
    }
  }, [timerDurationExpiresAt]);

  useEffect(() => {
    if (error?.code && RESEND_CODE_ERROR_CODE_DISABLE?.includes(error?.code)) {
      setIsDisabledResendCode(true);
    } else {
      setIsDisabledResendCode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error?.code]);

  // TRN-7406: the paid-code limit blocks resending but not switching to a free method.
  const isDisabledResendOnly = RESEND_ONLY_ERROR_CODE_DISABLE.includes(error?.code);

  return (
    <div
      className={
        isMessenger
          ? 'sms-code-wrapper-messenger'
          : isCardCreationForm
            ? 'sms-code-wrapper-card-creation-form'
            : 'sms-code-wrapper'
      }
    >
      {/*
        TRN-7254 bugfix: the warning case already gets its own caption right under the PIN boxes
        below (`security-error-caption`, per Figma) — rendering the Alert banner too showed the
        same "Security code has expired"/etc. text twice on screen. Non-warning alerts (e.g. the
        "code resent" success message) have no such caption, so they still go through Alert.
      */}
      {alertState.type !== 'warning' && <Alert type={alertState.type} message={i18n.getMessage(alertState.message)} />}
      {/*
        TRN-7254: the new 2FA modal shows this same "sent to {destination}" copy as the modal's
        header subtitle instead (see Sms.js) — hideDescription lets that call site skip the
        duplicate. Defaults to false so every other existing consumer is unaffected.
      */}
      {!hideDescription && (
        <div
          className={
            type === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && isMessenger
              ? 'sms-description-messenger push-notification-margin'
              : type === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && !isMessenger
                ? 'sms-description push-notification-margin'
                : 'sms-description'
          }
        >
          <ComplexTranslate
            text={
              type === TWO_FACTOR_AUTH_TYPES.SMS
                ? 'sms.description.sms'
                : type === TWO_FACTOR_AUTH_TYPES.WHATSAPP
                  ? 'sms.description.whatsapp'
                  : type === TWO_FACTOR_AUTH_TYPES.EMAIL
                    ? 'sms.description.email'
                    : type === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION
                      ? 'sms.description.pushNotification'
                      : isGoogle2FASignUpPopUp
                        ? '2fa.signup.description'
                        : '2fa.auth.description'
            }
            interpolation={{
              phone: PhoneComponent
            }}
          />
        </div>
      )}
      {type !== TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && (
        <>
          <SecurityInput
            disabled={isDisabled}
            type={'sms'}
            error={alertState.type}
            onComplete={onComplete}
            confirmationHandleType={confirmationHandleType}
          />
          {/* TRN-7254: new design also shows a short caption under the PIN boxes, in addition to the Alert above. */}
          {alertState.type === 'warning' && (
            <p className={'security-error-caption'}>{i18n.getMessage(alertState.message)}</p>
          )}
        </>
      )}
      {(isSwitchToAnotherConfirmationMethod ||
        (!isSwitchToAnotherConfirmationMethod &&
          (type === TWO_FACTOR_AUTH_TYPES.SMS ||
            type === TWO_FACTOR_AUTH_TYPES.WHATSAPP ||
            type === TWO_FACTOR_AUTH_TYPES.EMAIL))) && (
        <div className={'resend-wrapper'}>
          {type === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH ? (
            // TRN-7254: Google Authenticator's code is generated locally, not "sent" anywhere, so
            // there's nothing to resend and no "Didn't receive a code?" lead-in makes sense here —
            // just the switch-method link. Deliberately NOT branching on `showCountDown` like the
            // other types below: that state is owned by the parent (e.g.
            // PopUpInternalActionConfirmationScheme) and only ever gets set to `true` for non-Google
            // types, never reset back to `false` when switching back to Google Auth — so reusing it
            // here made this block's content depend on whatever type was last selected before Google.
            isSwitchToAnotherConfirmationMethod && (
              <button
                className={'unset link-normal without-margin'}
                onClick={handleSwitchToAnotherMethod}
                disabled={isDisabledResendCode}
              >
                {i18n.getMessage('sms.switchToAnotherMethod')}
              </button>
            )
          ) : !showCountDown ? (
            <ComplexTranslate
              text={'sms.description.resend'}
              interpolation={{
                resendCode:
                  type === TWO_FACTOR_AUTH_TYPES.SMS ||
                  type === TWO_FACTOR_AUTH_TYPES.WHATSAPP ||
                  type === TWO_FACTOR_AUTH_TYPES.EMAIL ? (
                    <>
                      <button
                        className={'unset link-normal'}
                        disabled={isDisabledResendCode || isDisabledResendOnly}
                        onClick={(e) => handleResendCode(e)}
                      >
                        {i18n.getMessage('sms.resendCode')}
                      </button>
                      {isSwitchToAnotherConfirmationMethod && (
                        <>
                          <br />
                          <button
                            className={'unset link-normal without-margin'}
                            onClick={handleSwitchToAnotherMethod}
                            disabled={isDisabledResendCode}
                          >
                            {i18n.getMessage('sms.switchToAnotherMethod')}
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <span> {i18n.getMessage('sms.otherMethods')}</span>
                      {isSwitchToAnotherConfirmationMethod && (
                        <>
                          <br />
                          <button
                            className={'unset link-normal without-margin'}
                            onClick={handleSwitchToAnotherMethod}
                            disabled={isDisabledResendCode}
                          >
                            {i18n.getMessage('sms.switchToAnotherMethod')}
                          </button>
                        </>
                      )}
                    </>
                  )
              }}
            />
          ) : (
            <>
              <p className={'resend-info'}>
                {i18n.getMessage('sms.resentMessage') + ' '}
                <CountDown onEnd={onTimerEnd} duration={timerDurationSecondsLeft} isDisabled={isDisabled} />
              </p>

              {isSwitchToAnotherConfirmationMethod && (
                <>
                  <button
                    className={'unset link-normal without-margin'}
                    onClick={handleSwitchToAnotherMethod}
                    disabled={isDisabledResendCode}
                  >
                    {i18n.getMessage('sms.switchToAnotherMethod')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

ConfirmCodeWrapper.propTypes = {
  type: PropTypes.string,
  PhoneComponent: PropTypes.object,
  isDisabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onComplete: PropTypes.func,
  showCountDown: PropTypes.bool,
  handleResendCode: PropTypes.func,
  onTimerEnd: PropTypes.func,
  isSwitchToAnotherConfirmationMethod: PropTypes.bool,
  isMessenger: PropTypes.bool,
  isCardCreationForm: PropTypes.bool,
  isGoogle2FASignUpPopUp: PropTypes.bool,
  handleSwitchToAnotherMethod: PropTypes.func,
  confirmationHandleType: PropTypes.string,
  hideDescription: PropTypes.bool
};
